import { handleError } from '@/general/errorHandling'
import { useCruiseStore, useDetailsStore, useFlexFlightStore, useFlightStore, usePriceStore, useRailStore } from '..'
import { Direction } from '../../../bewotec/openapi'
import { COMFORTAI, IND, INDAI, PAUSCHAL, PAUSCHALAI } from '../../../general/master-data'
import { useAppStore } from '../../../store/app'
import { useCabinStore } from '../cabin'
import { CabinCategory } from '../cabin/interfaces'
import { DetailFlightPrices } from '../cruise/classes/DetailFlightPrices'
import { Extra } from '../cruise/interfaces'
import { IExpinetFlexFlightType } from '../flex-flight/interfaces'
import { IExpinetFlightTypeMap, IExpinetSelectedFlight } from '../flight/interfaces'
import { TransportationCategory } from '../interfaces'
import { getRailLabel, getRailTariff } from '../rail/helper.js'
import { IFetchRailOption, RailDirection } from '../rail/interfaces'
import { getBeverageText, makeDummyPassengers, makePaxIds, priceHelper } from './helper.js'
import {
  DisplayFunctionFlightPrice,
  IDetailCruiseExtras,
  IDisplayFunctionsCabinCategory,
  IDisplayFunctionsFlightPrice,
  IFlightPriceComparison,
  IOrderFormData,
  IPriceItem,
  IService,
  ITransportationTotal,
  ShipServiceParams,
} from './interfaces'

export const category = () => {
  const detailsStore = useDetailsStore()
  const priceStore = usePriceStore()

  const priceModel = detailsStore.priceModel!

  const categoryPrice = (category: CabinCategory) => {
    return category.price
  }

  const categoryFlightPrice = (category: CabinCategory) => {
    if (detailsStore.mandatoryFlightForPriceModel) {
      const cheapestFlightPrices = priceStore.cheapestFlightPrices
      const cheapestFlightPrice = !!cheapestFlightPrices
        ? cheapestFlightPrices[priceModel as keyof DetailFlightPrices]
        : 0

      return cheapestFlightPrice ? category.price + cheapestFlightPrice : 0
    }

    return category.price
  }

  const displayFunctions: IDisplayFunctionsCabinCategory = {
    COMAI: categoryPrice,
    INDAI: categoryPrice,
    IND: categoryPrice,
    CLAAI: categoryPrice,
    CLA: categoryPrice,
    PAU: categoryFlightPrice,
    PAUAI: categoryFlightPrice,
    LIG: categoryPrice,
  }

  return displayFunctions[priceModel]
}

export const flight = () => {
  const detailsStore = useDetailsStore()
  const priceStore = usePriceStore()

  const priceModel = detailsStore.priceModel!

  const flightPrice = (flight: IExpinetFlightTypeMap | IExpinetFlexFlightType): IFlightPriceComparison => {
    return { amount: flight.price, prefix: false }
  }

  const compareFlightPrice = (flight: IExpinetFlightTypeMap | IExpinetFlexFlightType): IFlightPriceComparison => {
    let price = flight.price
    let prefix = false

    if (detailsStore.mandatoryFlightForPriceModel) {
      const cheapestFlightPrices = priceStore.cheapestFlightPrices
      const cheapestFlightPrice: number = cheapestFlightPrices
      ? cheapestFlightPrices[priceModel as keyof DetailFlightPrices]
      : 0
      
      price -= cheapestFlightPrice
      prefix = true
    }

    return { amount: price, prefix }
  }

  const displayFunctions: IDisplayFunctionsFlightPrice = {
    COMAI: flightPrice,
    INDAI: flightPrice,
    IND: flightPrice,
    CLAAI: flightPrice,
    CLA: flightPrice,
    PAU: compareFlightPrice,
    PAUAI: compareFlightPrice,
    LIG: flightPrice,
  }

  return displayFunctions[priceModel]
}

export const extrasForCabin = (): Extra[] => {
  const priceStore = usePriceStore()
  const detailsStore = useDetailsStore()
  const cabinStore = useCabinStore()

  const priceModel = detailsStore.priceModel
  const category = cabinStore.category
  const extras = priceStore.extras

  if (!!priceModel && !!category && !!extras) {
    return extras[priceModel as keyof IDetailCruiseExtras][category.id] ?? []
  }

  return []
}

export const cabinLabel = () => {
  const cabinStore = useCabinStore()
  const detailsStore = useDetailsStore()

  const priceModel = detailsStore.priceModel

  if (!priceModel) {
    return 'Kabine'
  }

  const category = cabinStore.category
  const categoryName = category?.name || category?.mainName || ''
  const cabin = cabinStore.cabin

  const flightIncluded =
    [PAUSCHAL, PAUSCHALAI].includes(priceModel) && detailsStore.mandatoryFlightForPriceModel ? ' inkl. AIDA-Flug' : ''
  const cabinNumber =
    [IND, INDAI, COMFORTAI].includes(priceModel) && !!cabin && cabinStore.selectedByUser ? ` Nr. ${cabin.id}` : ''
  const priceModelLabel = getBeverageText(priceModel)

  if ([COMFORTAI, INDAI, PAUSCHALAI].includes(priceModel)) {
    return `${categoryName}${cabinNumber}${flightIncluded} \r\n(${priceModelLabel})`
  } else {
    return `${categoryName}${cabinNumber}${flightIncluded} (${priceModelLabel})`
  }
}

export const cabinLabelPDF = () => {
  const detailsStore = useDetailsStore()
  const cabinStore = useCabinStore()

  const priceModel = detailsStore.priceModel

  if (!priceModel) {
    return 'Kabine'
  }

  const priceModelLabel: string = getBeverageText(priceModel)
  const category = cabinStore.category
  const categoryName = category?.name || category?.mainName || ''

  const flightIncluded =
    [PAUSCHAL, PAUSCHALAI].includes(priceModel) && detailsStore.mandatoryFlightForPriceModel ? ' inkl. AIDA-Flug' : ''
  const categoryId = ![COMFORTAI, INDAI, IND].includes(priceModel) || !category ? '' : ` ${category.id}`

  return `${categoryName}${categoryId}${flightIncluded} (Preistarif: ${priceModelLabel})`
}

export const cabinItems = () => {
  const detailsStore = useDetailsStore()
  const cabinStore = useCabinStore()

  const priceModel = detailsStore.priceModel
  const category = cabinStore.category

  if (!priceModel || !category) return []

  const paxConfig = detailsStore.paxConfig!
  const prices = category.pricings
  const priceItems: IPriceItem[] = []

  let i = 0

  for (let j = 0; j < paxConfig.adults; ++j) {
    priceItems.push({
      label: 'Erwachsener (ab 25 Jahre)',
      price: prices[i + j],
    })
  }

  i += paxConfig.adults

  for (let j = 0; j < paxConfig.juveniles; ++j) {
    priceItems.push({
      label: 'Jugendlicher (16 bis 24 Jahre)',
      price: prices[i + j],
    })
  }

  i += paxConfig.juveniles

  for (let j = 0; j < paxConfig.children; ++j) {
    priceItems.push({
      label: 'Kind (2 bis 15 Jahre)',
      price: prices[i + j],
    })
  }

  i += paxConfig.babies

  for (let j = 0; j < paxConfig.babies; ++j) {
    priceItems.push({
      label: 'Baby (6 bis 23 Monate)',
      // // otherwise we get NaN as price in the PDF
      // TODO: check if fallback is necessary, when MS is implemented
      price: prices[i + j] ?? 0,
    })
  }

  return priceItems
}

export const cabinTotal = () => {
  const priceStore = usePriceStore()

  const cabinItems: IPriceItem[] = priceStore.cabinItems
  return cabinItems.reduce((accumulator, item) => accumulator + item.price, 0)
}

export const transportationLabel = () => {
  const flightStore = useFlightStore()
  const flexFlightStore = useFlexFlightStore()
  const railStore = useRailStore()
  const detailsStore = useDetailsStore()

  const transportation = detailsStore.transportation
  const isRNF = flightStore.selectedRailOptionId !== ''
  const isFlexRNF = flexFlightStore.selectedRailOptionId !== ''

  if (transportation === 'Flug' && !isRNF) {
    const outboundFlight = flightStore.selectedFlight?.outboundFlight
    const inboundFlight = flightStore.selectedFlight?.inboundFlight

    if (!!outboundFlight || !!inboundFlight) {
      return 'An- und Abreise (Flug)'
    }
  } else if (transportation === 'Wunschflug' && !isFlexRNF) {
    const selectedFlight = flexFlightStore.selectedFlight

    if (!!selectedFlight) {
      return 'An- und Abreise (Wunschflug)'
    }
  } else if (transportation === 'Bahn') {
    const isRailOptionSelected = railStore.selectedRailOptionId !== ''
    const selectedRailOption = railStore.selectedRailOption

    if (isRailOptionSelected && !!selectedRailOption && 'railClassName' in selectedRailOption) {
      const tariff = getRailTariff(selectedRailOption.railTarifCode)

      return `An- und Abreise \n(Bahn ${selectedRailOption.railClassName} ${tariff})`
    }
  }

  return 'An- und Abreise'
}

export const transportationItems = (): IPriceItem[] => {
  const flightStore = useFlightStore()
  const flexFlightStore = useFlexFlightStore()
  const detailsStore = useDetailsStore()
  const priceStore = usePriceStore()

  const isRNF = flightStore.selectedRailOptionId !== ''
  const isFlexRNF = flexFlightStore.selectedRailOptionId !== ''
  const transportation: TransportationCategory = detailsStore.transportation

  if (transportation === 'Individuell') {
    return []
  } else if (transportation === 'Flug' && isRNF) {
    const flightPriceFunction: DisplayFunctionFlightPrice = priceStore.flight
    const selectedFlight: IExpinetSelectedFlight | null = flightStore.selectedFlight

    const outboundFlight: IExpinetFlightTypeMap = {
      outboundFlight: selectedFlight?.outboundFlight ?? null,
      inboundFlight: selectedFlight?.inboundFlight ?? null,
      price: selectedFlight?.outboundFlight?.price ?? 0,
      direction: Direction.O,
    }
    const inboundFlight: IExpinetFlightTypeMap = {
      outboundFlight: selectedFlight?.outboundFlight ?? null,
      inboundFlight: selectedFlight?.inboundFlight ?? null,
      price: selectedFlight?.inboundFlight?.price ?? 0,
      direction: Direction.R,
    }

    const selectedRailDirection: RailDirection = flightStore.railDirectionRNF
    const railDirection = getRailLabel(selectedRailDirection)
    let total = 0

    if (outboundFlight.outboundFlight?.direction === Direction.O) {
      const price = flightPriceFunction(outboundFlight)
      total += price.amount
    }
    if (inboundFlight.inboundFlight?.direction === Direction.R) {
      const price = flightPriceFunction(inboundFlight)
      total += price.amount
    } else {
      const price = flightPriceFunction(outboundFlight)
      total = price.amount
    }

    const selectedRailOption = flightStore.selectedRailOption
    const hasSelectedRailOption = !!selectedRailOption && 'railClassName' in selectedRailOption

    const items = [
      {
        label: 'Flug',
        price: total,
      },
    ]

    if (hasSelectedRailOption) {
      items.push({
        label: `Bahn (${selectedRailOption.railClassName}, ${railDirection})`,
        price: selectedRailOption?.price,
      })
    }

    return items
  } else if (transportation === 'Wunschflug' && isFlexRNF) {
    const flightPriceFunction: DisplayFunctionFlightPrice = priceStore.flight
    const selectedFlight: IExpinetFlexFlightType | null = flexFlightStore.selectedFlight
    const selectedRailDirection: string = flexFlightStore.railDirection
    const railDirection = getRailLabel(selectedRailDirection)
    let total = 0

    if (!!selectedFlight) {
      const price = flightPriceFunction(selectedFlight)
      total += price.amount
    }

    const selectedRailOption = flexFlightStore.selectedRailOption
    const items = [
      {
        label: 'Wunschflug',
        price: total,
      },
    ]

    if (!!selectedRailOption && 'railClassName' in selectedRailOption) {
      items.push({
        label: `Bahn (${selectedRailOption.railClassName}, ${railDirection})`,
        price: selectedRailOption.price,
      })
    }

    return items
  }

  return []
}

export const transportationTotal = (): ITransportationTotal => {
  const detailsStore = useDetailsStore()
  const flightStore = useFlightStore()
  const priceStore = usePriceStore()
  const flexFlightStore = useFlexFlightStore()
  const railStore = useRailStore()

  if (!detailsStore.priceModel) {
    return { amount: 0, prefix: false }
  }

  const transportation: TransportationCategory = detailsStore.transportation

  let total = 0
  let prefix = false

  if (transportation === 'Flug') {
    const flightPriceFunction: DisplayFunctionFlightPrice = priceStore.flight

    const selectedFlight: IExpinetSelectedFlight | null = flightStore.selectedFlight

    const outboundFlight: IExpinetFlightTypeMap = {
      outboundFlight: selectedFlight?.outboundFlight ?? null,
      inboundFlight: selectedFlight?.inboundFlight ?? null,
      price: selectedFlight?.outboundFlight?.price ?? 0,
      direction: Direction.O,
    }
    const inboundFlight: IExpinetFlightTypeMap = {
      outboundFlight: selectedFlight?.outboundFlight ?? null,
      inboundFlight: selectedFlight?.inboundFlight ?? null,
      price: selectedFlight?.inboundFlight?.price ?? 0,
      direction: Direction.R,
    }

    if (outboundFlight.outboundFlight?.direction === Direction.O) {
      const price = flightPriceFunction(outboundFlight)
      total += price.amount
      prefix = price.prefix
    } else if (inboundFlight.inboundFlight?.direction === Direction.R) {
      const price = flightPriceFunction(inboundFlight)
      total += price.amount
      prefix = price.prefix
    } else {
      const price = flightPriceFunction(outboundFlight)
      total = price.amount
      prefix = price.prefix
    }

    const isRNF = flightStore.selectedRailOptionId !== ''

    if (isRNF) {
      const selectedRailOption = flightStore.selectedRailOption as IFetchRailOption | null

      if (!!selectedRailOption?.price) {
        total += selectedRailOption.price
      }
    }
  } else if (transportation === 'Wunschflug') {
    const flightPriceFunction: DisplayFunctionFlightPrice = priceStore.flight

    const selectedFlight: IExpinetFlexFlightType | null = flexFlightStore.selectedFlight

    if (!!selectedFlight) {
      const price = flightPriceFunction(selectedFlight)
      total += price.amount
      prefix = price.prefix
    }

    const isFlexRNF = flexFlightStore.selectedRailOptionId !== ''

    if (isFlexRNF) {
      const selectedRailOption = flexFlightStore.selectedRailOption as IFetchRailOption | null

      if (!!selectedRailOption?.price) {
        total += selectedRailOption.price
      }
    }
  } else if (transportation === 'Bahn') {
    const option = railStore.selectedRailOption as IFetchRailOption | null

    if (!!option?.price) {
      total = option.price
    }
  }

  return { amount: total, prefix }
}

export const total = () => {
  const priceStore = usePriceStore()

  const cabinTotal: number = priceStore.cabinTotal
  const transportationTotal: ITransportationTotal = priceStore.transportationTotal

  return !!transportationTotal.amount ? cabinTotal + transportationTotal.amount : cabinTotal
}

export const orderFormData = (): IOrderFormData => {
  const detailsStore = useDetailsStore()
  const flightStore = useFlightStore()
  const flexFlightStore = useFlexFlightStore()
  const railStore = useRailStore()

  const paxConfig = detailsStore.paxConfig!
  const priceModel = detailsStore.priceModel!
  const transportation = detailsStore.transportation

  let services: IService[] = []

  try {
    services = getServices()
  } catch (error) {
    const errorOrigin = detailsStore.failedServiceForBooking
      ? `orderFormData - ${detailsStore.failedServiceForBooking}`
      : 'orderFormData'

    handleError({ error, origin: errorOrigin })
  }

  const railAdults =
    transportation === 'Bahn' ? railStore.railAdults : transportation === 'Flug' ? flightStore.railAdults : 0
  const birthdates = flexFlightStore.birthdates
  const dummyPassengers = makeDummyPassengers(paxConfig, services.length, birthdates, railAdults)
  const isFlexFlightSelected = detailsStore.transportation === 'Wunschflug' && !!flexFlightStore.selectedFlight

  const formData: IOrderFormData = {
    action: isFlexFlightSelected ? 'G' : 'BA',
    cruiseline: 'AID',
    bkgno: null,
    multiFunctionLine: priceModel,
    numPaxe: paxConfig.total,
    services,
    paxe: dummyPassengers,
  }

  // @ts-ignore
  const optanonConsent: string | undefined = window._satellite?.cookie?.get('OptanonConsent')
  const prevPage = window.location.href

  if (!!optanonConsent && optanonConsent.includes('C0002:1') && optanonConsent.includes('C0004:1')) {
    // @ts-ignore
    const visitor = window.Visitor.getInstance('BCF356A25FBF85370A495EA3@AdobeOrg', {
      overwriteCrossDomainMCIDAndAID: true,
    })

    const visitorIds = visitor.appendVisitorIDsTo('').split('=')[1]

    formData.adobe_mc = visitorIds
    formData.OptanonConsent = encodeURIComponent(optanonConsent)
    formData.prev_page = encodeURIComponent(prevPage)
  }

  return formData
}

export const getServices = (): IService[] => {
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()
  const cabinStore = useCabinStore()
  const priceStore = usePriceStore()
  const flightStore = useFlightStore()
  const flexFlightStore = useFlexFlightStore()
  const railStore = useRailStore()

  detailsStore.failedServiceForBooking = ''

  const transportation = detailsStore.transportation
  const paxConfig = detailsStore.paxConfig!
  const priceModel = detailsStore.priceModel!
  const isPremium = [IND, INDAI].includes(priceModel)
  const isComfort = [COMFORTAI].includes(priceModel)
  const isCabinSelectable = isPremium || isComfort
  const paxIds = makePaxIds(paxConfig)

  let services: IService[] = []

  const shipServiceParams: ShipServiceParams = {
    paxIds,
    paxConfig,
    isCabinSelectable,
    ship: cruiseStore.ship!,
    cruise: cruiseStore.cruise!,
    category: cabinStore.category,
    cabin: cabinStore.cabin,
  }

  const shipService = priceHelper.makeShipService(shipServiceParams)

  const hasFlightData = transportation === 'Flug' && !!flightStore.selectedFlight
  const flightServices = hasFlightData ? priceStore.getFlightServices(paxIds, paxConfig) : []

  const hasFlexFlightData = transportation === 'Wunschflug' && !!flexFlightStore.selectedFlight
  const flexFlightServices = hasFlexFlightData ? priceStore.getFlexFlightServices(paxIds, paxConfig) : []

  const RITOption = railStore.selectedRailOption
  const hasRITClassSelected = !!RITOption && 'railClassCode' in RITOption && !!railStore.selectedRailOptionId
  const railService =
    transportation === 'Bahn' && hasRITClassSelected ? priceStore.getRITService(paxIds, paxConfig) : null

  services.push(shipService)
  services = services.concat(flightServices, flexFlightServices)

  if (!!railService) {
    services.push(railService)
  }

  return services
}

export const orderFormUrl = () => {
  const appStore = useAppStore()

  let url =
    process.env.STAGE === 'prod'
      ? 'https://buchen.aida.de/wfe-ash/portal/buchung2?_form=BaseForm&_stylesheet=toma'
      : 'https://itg-buchen.aida.de/wfe-ash/portal/buchung2?_form=BaseForm&_stylesheet=toma'

  if (!!appStore.bosysReference) {
    url += '&reference=' + appStore.bosysReference
  }

  return url
}

export default {
  category,
  flight,
  extrasForCabin,
  cabinLabel,
  cabinLabelPDF,
  cabinItems,
  cabinTotal,
  transportationLabel,
  transportationItems,
  transportationTotal,
  total,
  orderFormData,
  getServices,
  orderFormUrl,
}
